import React, { useCallback } from 'react'

import classnames from 'classnames'

import { t } from 'src/translations'

export type Option = {
  name: string
  position: number
  icon?: React.ReactElement
  value: string
  dataTestId?: string
  disabled?: boolean
}

type Props = {
  value: Option
  options: Option[]
  onChange: (position: Option) => void
  className?: string
  themed?: boolean
  disabled?: boolean
}

const Slider = (props: Props) => {
  const selected = props.value

  const select = useCallback(
    (side: number) => () => {
      const option = props.options[side]
      if (option?.disabled) return
      props.onChange(option ?? ({} as Option))
    },
    [props]
  )

  const margin = 4 // px
  const numOptions = props.options.length
  return (
    <div
      className={classnames(
        'slider',
        { plain: !props.themed },
        selected,
        props.className
      )}
      role='group'
    >
      <div
        className={classnames('switch')}
        style={{
          width: `calc(${100 / numOptions}% - ${margin * 2}px)`,
          left: `calc(${
            (100 / numOptions) * props.value.position
          }% + ${margin}px)`
        }}
      />
      {props.options.map((option: Option, key: number) => {
        const isOptionDisabled = props.disabled || option.disabled
        return (
          <button
            disabled={isOptionDisabled}
            key={option.value}
            type='button'
            className={classnames('option', {
              selected: selected.position === key,
              plain: selected.position !== key && !props.themed,
              disabled: isOptionDisabled
            })}
            onClick={select(key)}
            data-testid={option.value}
            aria-pressed={selected.position === key}
            aria-label={t('slider.switch-to', { name: option.name })}
          >
            {option.icon ? option.icon : null}
            <div className='optionText'>
              <div>{option.name}</div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default Slider
